/**
 * Rental Listing Manager - Landlord Component
 * Allows landlords to list their vacant units on the marketplace
 */

import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  PlusCircle, Home, Trash2, Edit2, Eye, MapPin,
  Bed, Bath, Square, DollarSign, Calendar, Image,
  X, CheckCircle, AlertCircle, ExternalLink
} from 'lucide-react';

const RentalListingManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [properties, setProperties] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    property_type: 'apartment',
    bedrooms: 1,
    bathrooms: 1,
    sqft: 800,
    rent: 1500,
    deposit: 1500,
    location: '',
    address: '',
    description: '',
    amenities: [],
    images: [],
    available_date: new Date().toISOString().split('T')[0],
    lease_terms: ['12 months'],
    utilities_included: [],
    contact_name: '',
    contact_phone: '',
    contact_email: ''
  });

  const amenityOptions = [
    'Air Conditioning', 'In-Unit Laundry', 'Parking', 'Pool Access', 
    'Gym', 'Pet Friendly', 'Balcony', 'Storage', 'Gated Community',
    'Furnished Available', 'Dishwasher', 'Garbage Disposal'
  ];

  const utilityOptions = ['Water', 'Trash', 'Cable', 'Internet', 'Electric', 'Gas'];
  const leaseOptions = ['Month-to-Month', '6 months', '12 months', '24 months'];

  useEffect(() => {
    fetchListings();
    fetchInquiries();
    fetchProperties();
  }, []);

  const fetchListings = async () => {
    try {
      const { data } = await api.get('/rental-marketplace/my-listings');
      setListings(data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Failed to load your listings');
    }
    setLoading(false);
  };

  const fetchInquiries = async () => {
    try {
      const { data } = await api.get('/rental-marketplace/inquiries');
      setInquiries(data.inquiries || []);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const { data } = await api.get('/landlord/properties');
      setProperties(data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingListing) {
        // Update existing listing
        await api.put(`/rental-marketplace/listings/${editingListing.id}`, formData);
        toast.success('Listing updated successfully');
      } else {
        // Create new listing
        await api.post('/rental-marketplace/listings', formData);
        toast.success('Listing created successfully! It is now live on the marketplace.');
      }
      
      setShowForm(false);
      setEditingListing(null);
      resetForm();
      fetchListings();
    } catch (error) {
      console.error('Error saving listing:', error);
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await api.delete(`/rental-marketplace/listings/${listingId}`);
      toast.success('Listing deleted successfully');
      fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    }
  };

  const handleEdit = (listing) => {
    setFormData({
      title: listing.title || '',
      property_type: listing.type || 'apartment',
      bedrooms: listing.bedrooms || 1,
      bathrooms: listing.bathrooms || 1,
      sqft: listing.sqft || 800,
      rent: listing.rent || 1500,
      deposit: listing.deposit || 1500,
      location: listing.location || '',
      address: listing.address || '',
      description: listing.description || '',
      amenities: listing.amenities || [],
      images: listing.images || [],
      available_date: listing.available_date || new Date().toISOString().split('T')[0],
      lease_terms: listing.lease_terms || ['12 months'],
      utilities_included: listing.utilities_included || [],
      contact_name: listing.contact_name || '',
      contact_phone: listing.contact_phone || '',
      contact_email: listing.contact_email || ''
    });
    setEditingListing(listing);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      property_type: 'apartment',
      bedrooms: 1,
      bathrooms: 1,
      sqft: 800,
      rent: 1500,
      deposit: 1500,
      location: '',
      address: '',
      description: '',
      amenities: [],
      images: [],
      available_date: new Date().toISOString().split('T')[0],
      lease_terms: ['12 months'],
      utilities_included: [],
      contact_name: '',
      contact_phone: '',
      contact_email: ''
    });
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const toggleUtility = (utility) => {
    setFormData(prev => ({
      ...prev,
      utilities_included: prev.utilities_included.includes(utility)
        ? prev.utilities_included.filter(u => u !== utility)
        : [...prev.utilities_included, utility]
    }));
  };

  const toggleLeaseTerm = (term) => {
    setFormData(prev => ({
      ...prev,
      lease_terms: prev.lease_terms.includes(term)
        ? prev.lease_terms.filter(t => t !== term)
        : [...prev.lease_terms, term]
    }));
  };

  const addImageUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url]
      }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Leasing Hub Listings</h2>
          <p className="text-gray-600">List your vacant units to find qualified tenants</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingListing(null); setShowForm(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="create-listing-btn"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          List Vacant Unit
        </Button>
      </div>

      {/* Inquiries Alert */}
      {inquiries.filter(i => i.status === 'new').length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <div>
                <span className="font-semibold text-blue-800">
                  You have {inquiries.filter(i => i.status === 'new').length} new inquiry(s)!
                </span>
                <p className="text-sm text-blue-600">
                  Potential tenants are interested in your listings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Listings Grid */}
      {listings.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map(listing => (
            <Card key={listing.id} className="overflow-hidden" data-testid={`my-listing-${listing.id}`}>
              <div className="relative h-40">
                <img 
                  src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
                <Badge 
                  className={`absolute top-2 right-2 ${listing.status === 'active' ? 'bg-emerald-500' : 'bg-gray-500'}`}
                >
                  {listing.status === 'active' ? 'Active' : listing.status}
                </Badge>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-1 line-clamp-1">{listing.title}</h3>
                <div className="flex items-center text-gray-500 text-sm mb-2">
                  <MapPin className="h-3 w-3 mr-1" />
                  {listing.location}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                  <span className="flex items-center gap-1">
                    <Bed className="h-3 w-3" /> {listing.bedrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath className="h-3 w-3" /> {listing.bathrooms}
                  </span>
                  <span className="flex items-center gap-1">
                    <Square className="h-3 w-3" /> {listing.sqft}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-bold text-emerald-600">
                    ${listing.rent?.toLocaleString()}/mo
                  </span>
                  <span className="text-sm text-gray-500">
                    {listing.views || 0} views · {listing.inquiries || 0} inquiries
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleEdit(listing)}
                  >
                    <Edit2 className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open('/rentals', '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(listing.id)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <CardContent>
            <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No listings yet</h3>
            <p className="text-gray-500 mb-4">
              Create your first listing to advertise your vacant units on the marketplace
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <PlusCircle className="h-4 w-4 mr-2" />
              Create Your First Listing
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Inquiries Section */}
      {inquiries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              Recent Inquiries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {inquiries.slice(0, 5).map(inquiry => (
                <div key={inquiry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-semibold">{inquiry.name}</p>
                    <p className="text-sm text-gray-500">Interested in: {inquiry.listing_title}</p>
                    <p className="text-sm text-gray-500">{inquiry.email} · {inquiry.phone}</p>
                  </div>
                  <Badge className={inquiry.status === 'new' ? 'bg-blue-500' : 'bg-gray-400'}>
                    {inquiry.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">
                {editingListing ? 'Edit Listing' : 'Create New Listing'}
              </h2>
              <button onClick={() => { setShowForm(false); setEditingListing(null); }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Property Details</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Listing Title *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Modern 2BR Apartment with Ocean View"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Property Type *</label>
                    <select
                      value={formData.property_type}
                      onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded"
                      required
                    >
                      <option value="apartment">Apartment</option>
                      <option value="condo">Condo</option>
                      <option value="house">House</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="studio">Studio</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Available Date *</label>
                    <Input
                      type="date"
                      value={formData.available_date}
                      onChange={(e) => setFormData({...formData, available_date: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Bedrooms</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({...formData, bedrooms: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bathrooms</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({...formData, bathrooms: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Sq Ft</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.sqft}
                      onChange={(e) => setFormData({...formData, sqft: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Monthly Rent ($)</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.rent}
                      onChange={(e) => setFormData({...formData, rent: parseFloat(e.target.value)})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Security Deposit ($)</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.deposit}
                    onChange={(e) => setFormData({...formData, deposit: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Location</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Address *</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main Street, Unit 4A"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">City/Area *</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="e.g., Cable Beach, Nassau"
                    required
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Description</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Property Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    rows={4}
                    placeholder="Describe the property features, neighborhood, and any special highlights..."
                    required
                  />
                </div>
              </div>

              {/* Amenities */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Amenities</h3>
                <div className="flex flex-wrap gap-2">
                  {amenityOptions.map(amenity => (
                    <Badge
                      key={amenity}
                      className={`cursor-pointer ${formData.amenities.includes(amenity) ? 'bg-emerald-600' : 'bg-gray-200 text-gray-700'}`}
                      onClick={() => toggleAmenity(amenity)}
                    >
                      {formData.amenities.includes(amenity) && <CheckCircle className="h-3 w-3 mr-1" />}
                      {amenity}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Lease Terms */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Lease Terms</h3>
                <div className="flex flex-wrap gap-2">
                  {leaseOptions.map(term => (
                    <Badge
                      key={term}
                      className={`cursor-pointer ${formData.lease_terms.includes(term) ? 'bg-blue-600' : 'bg-gray-200 text-gray-700'}`}
                      onClick={() => toggleLeaseTerm(term)}
                    >
                      {formData.lease_terms.includes(term) && <CheckCircle className="h-3 w-3 mr-1" />}
                      {term}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Utilities */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Utilities Included</h3>
                <div className="flex flex-wrap gap-2">
                  {utilityOptions.map(utility => (
                    <Badge
                      key={utility}
                      className={`cursor-pointer ${formData.utilities_included.includes(utility) ? 'bg-purple-600' : 'bg-gray-200 text-gray-700'}`}
                      onClick={() => toggleUtility(utility)}
                    >
                      {formData.utilities_included.includes(utility) && <CheckCircle className="h-3 w-3 mr-1" />}
                      {utility}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Photos</h3>
                <div className="flex flex-wrap gap-3">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative w-24 h-24">
                      <img src={img} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="w-24 h-24 border-2 border-dashed border-gray-300 rounded flex items-center justify-center hover:bg-gray-50"
                  >
                    <Image className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
                <p className="text-xs text-gray-500">Add image URLs for your property photos</p>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Contact Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Name *</label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Contact Phone *</label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      placeholder="+1-242-555-1234"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Contact Email</label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingListing ? 'Update Listing' : 'Publish Listing'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => { setShowForm(false); setEditingListing(null); }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalListingManager;
